/**
 * API Configuration Constants
 * Centralizes API endpoint patterns and configuration
 */

export const API_CONFIG = {
  /**
   * Enable cache-busting query parameters
   */
  ENABLE_CACHE_BUSTING: true,

  /**
   * Default request timeout in milliseconds
   */
  DEFAULT_TIMEOUT: 10000,
} as const;

/**
 * Determines if running in development environment
 *
 * @returns true if NODE_ENV is 'development'
 */
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

/**
 * Opt-in flag (`REACT_APP_USE_STAGING_DATA=true yarn start`) that points local
 * dev at the published data files instead of the fixtures in `public/dev/json`.
 * The request stays on the relative `/data/...` path either way — `src/setupProxy.js`
 * forwards it to the staging origin — so dev exercises the same code path and the
 * same data as staging.
 *
 * @returns true when local dev should read the published data files
 */
export const usesStagingData = (): boolean => {
  return process.env.REACT_APP_USE_STAGING_DATA === 'true';
};

/**
 * Whether to read the committed dev fixtures. Development does by default; the
 * opt-in flag above sends it to the published files instead, and every other
 * environment always reads those.
 *
 * @returns true when the local fixture endpoints should be used
 */
export const usesLocalFixtures = (): boolean => {
  return isDevelopment() && !usesStagingData();
};

/**
 * Public Schedule API endpoint patterns
 * Returns URLs based on environment (development vs production)
 */
export const PUBLIC_SCHEDULE_ENDPOINTS = {
  /**
   * Development endpoint pattern
   *
   * @param key - Schedule key (e.g., 'academy-division-26')
   */
  DEVELOPMENT: (key: string) => `/dev/json/schedule/${key}.json`,

  /**
   * Production endpoint pattern
   *
   * @param key - Schedule key (e.g., 'academy-division-26')
   */
  PRODUCTION: (key: string) => `/data/schedule/${key}.json`,
} as const;

/**
 * Public Standings API endpoint patterns
 * Keyed solely by the season slug (competition_seasons.key) — there is no
 * separate season segment, unlike the schedule endpoints.
 * Returns URLs based on environment (development vs production)
 */
export const PUBLIC_STANDINGS_ENDPOINTS = {
  /**
   * Development endpoint pattern
   *
   * @param key - Season key slug (e.g., 'academy-division-26')
   */
  DEVELOPMENT: (key: string) => `/dev/json/standings/${key}.json`,

  /**
   * Production endpoint pattern
   *
   * @param key - Season key slug (e.g., 'academy-division-26')
   */
  PRODUCTION: (key: string) => `/data/standings/${key}.json`,
} as const;
